import { defineStore } from 'pinia';
import { hydrated } from '@/storePinia';

import { ILang } from '@/storePinia/stores/results/ISiteLanguages';
import { IDeliveryCountry } from '@/storePinia/stores/results/IDeliveryCountries';
import axios from 'axios';
import useLoader from '@use/useLoader/useLoader';
import { IProduct, IProductSize } from '@/storePinia/interfaces/IProduct/IProduct';
import IResults from './IResults';

type ResultsGetters = {
  getRequestLang(state: IResults): { LANGUAGE_ID: string };
  getCurrentLang(state: IResults): ILang | undefined;
  isProd(state: IResults): boolean;
  getSelectedCountry(state: IResults): IDeliveryCountry | undefined;
};

type ResultsActions = {
  shareProductChangeSKU(payload: { product: IProduct; size: IProductSize }): Promise<void>;
};

const loader = useLoader();

export const useResultsStore = defineStore<
  'results', // ID стора
  IResults, // Тип для state
  ResultsGetters,
  ResultsActions // Типи для геттерів
>('results', {
  state: (): IResults => ({
    routes: hydrated<IResults['routes']>(),
    headSearch: hydrated<IResults['headSearch']>(),
    storeInformation: hydrated<IResults['storeInformation']>(),
    messages: {},
    subscribe: hydrated<IResults['subscribe']>(),
    siteSettings: hydrated<IResults['siteSettings']>(),
    deliveryCountries: hydrated<IResults['deliveryCountries']>(),
    siteLanguages: hydrated<IResults['siteLanguages']>(),
    googleMapsKeys: hydrated<IResults['googleMapsKeys']>(),
    socialServices: {},
    allLikes: {},
    signInBanners: [],
    menus: {},
    mainSliderActivities: {},
    instagramLooks: {},
    seoText: '',
    cabinetShare: {},
    breadcrumbs: [],
    blogElement: {},
    newsElement: {},
    action: {},
    blogMenu: [],
    howProductsMakeBanner: {},
    siblings: [],
    diamondsLandingBanners: [],
    allusion: {},
    diamondCertificate: {},
  }),
  getters: {
    getRequestLang: (state): { LANGUAGE_ID: string } => {
      let code = '';
      if (state.siteLanguages?.items) {
        const activeLanguage = Object.values(state.siteLanguages.items).find((el) => el.current);
        if (activeLanguage?.code) {
          code = activeLanguage?.code;
        }
      }
      return { LANGUAGE_ID: code };
    },
    getCurrentLang: (state): ILang | undefined => {
      if (state.siteLanguages?.items) {
        return Object.values(state.siteLanguages?.items)?.find((el) => el.current);
      }
      return undefined;
    },
    isProd: (state): boolean => state.siteLanguages?.isProd || false,
    getSelectedCountry: (state): IDeliveryCountry | undefined =>
      state.deliveryCountries?.items?.find((el) => el.isUserCountry),
  },
  actions: {
    async shareProductChangeSKU({ product, size }) {
      try {
        loader.start();
        const url = size.detailPage;
        const { data } = await axios.get(url, { params: { api: 'y' } });

        const newData = {
          ...this.cabinetShare,
          items: this.cabinetShare?.items?.map((el) => {
            if (el.id === product.id) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              return data?.product;
            }
            return el;
          }),
        };

        this.$patch({ cabinetShare: newData });
        loader.stop();
      } catch (err) {
        console.error(err);
      } finally {
        loader.stop();
      }
    },
  },
});
